import { Box, Typography, useTheme, Paper } from "@mui/material";
import { TableHeaderContainer } from "../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../components/container/TableTitleContainer";
import { toPng } from "html-to-image";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Sankey, { rootNodeBracket } from "../components/chart/Sankey";
import ComboBox from "../components/input/ComboBox";
import { EnvFactor } from "../db_access/env_factor";
import { getOrgTokens, ORG_PALETTE } from "../theme/style/organizationPalette";
import "../style/components-style/SankeyDiagram.css";

const SankeyDiagram = forwardRef(({ processData, envfactors }, parentRef) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const [sankeyData, setSankeyData] = useState({ nodes: [], links: [] });
  const clearSankeyData = () => setSankeyData({ nodes: [], links: [] });
  const ref = useRef(null);
  const project_elments = { label: "Project Elements", value: "Project Elements" };
  // const leed_category = { label: "LEED Category", value: "LEED Category" };
  const level2Options = [project_elments];
  const levels = ["LCA Stages", "Project Elements", "Categories", "Material"];
  const [level2, setLevel2] = useState(project_elments.value);
  const [totalRootValue, setTotalRootValue] = useState(0);
  const [suppressedMaterials, setSuppressedMaterials] = useState([]);

  useImperativeHandle(parentRef, () => ({
    getScreenshot: async () => {
      if (ref.current) {
        return await toPng(ref.current, { cacheBust: true, backgroundColor: "#ffffff" });
      }
      return null;
    },
  }));
  const mergeLinks = (links) => {
    const root = new Map();
    for (const link of links) {
      const { source, target, value = 0 } = link;
      if (!source || !target) continue;
      if (!root.has(source)) {
        root.set(source, new Map());
      }
      const inner = root.get(source);
      if (inner.has(target)) {
        inner.get(target).value += value;
      } else {
        inner.set(target, { ...link });
      }
    }
    const result = [];
    for (const inner of root.values()) {
      for (const link of inner.values()) {
        result.push(link);
      }
    }
    return result;
  };
  const getEnvFactors = useCallback(async () => {
    if (processData.length < 1) return [];
    if (envfactors) return envfactors;
    const ids = processData.map((p) => p.id);
    const res = await EnvFactor.get_by_process_ids(ids);
    return await res.json();
  }, [processData, envfactors]);
  const getSankeyData = useCallback(async () => {
    const envfactors = await getEnvFactors();
    if (!envfactors || envfactors.length < 1) {
      clearSankeyData();
      return;
    }

    const processElementBracket = "__projectElement__";
    const materialCategoryBracket = "__materialCategory__";
    const leedCategoryBracket = "__leedCategory__";
    const materialBracket = "__material__";
    const A1_A3 = { id: rootNodeBracket + "A1 - A3" + rootNodeBracket, label: "A1 - A3" };
    const A4_A5 = { id: rootNodeBracket + "A4 - A5" + rootNodeBracket, label: "A4 - A5" };
    const B = { id: rootNodeBracket + "B" + rootNodeBracket, label: "B" };
    const C = { id: rootNodeBracket + "C" + rootNodeBracket, label: "C" };
    const D = { id: rootNodeBracket + "D" + rootNodeBracket, label: "D" };
    const rootNodes = [A1_A3, A4_A5, B, C, D];
    const getlcaStageCode = (lca_stage) => {
      if (!lca_stage) return null;
      if (typeof lca_stage !== "string") return null;
      if (lca_stage.startsWith("A1-A3")) return A1_A3;
      if (["A1", "A2", "A3"].includes(lca_stage.substring(0, 2))) return A1_A3;
      if (["A4", "A5"].includes(lca_stage.substring(0, 2))) return A4_A5;
      if (lca_stage.startsWith("B")) return B;
      if (lca_stage.startsWith("C")) return C;
      if (lca_stage.startsWith("D")) return D;
      return null;
    };
    const getlcaStageDescription = (lca_stage) => {
      if (!lca_stage) return null;
      if (typeof lca_stage !== "string") return null;
      let description = null;
      if (lca_stage.startsWith("A1-A3")) description = "A1 - A3";
      if (["A1", "A2", "A3"].includes(lca_stage.substring(0, 2))) description = lca_stage.substring(3).trim();
      if (["A4", "A5"].includes(lca_stage.substring(0, 2))) description = lca_stage.substring(3).trim();
      if (lca_stage.startsWith("B")) description = lca_stage.substring(3).trim();
      if (lca_stage.startsWith("C")) description = lca_stage.substring(3).trim();
      if (lca_stage.startsWith("D")) description = lca_stage.substring(2).trim();
      if (!description) return null;
      return { id: processElementBracket + description + processElementBracket, label: description };
    };
    const projectElementLinks = processData
      .filter((p) => p.gwp > 0)
      .map((p) => {
        const value = p.gwp;
        const lcaStage = p.lca_stage;
        const source = getlcaStageCode(lcaStage)?.id;
        const target = getlcaStageDescription(lcaStage)?.id;
        if (!target) {
          console.log("undefined target for projectElementLinks:", p, source, value);
        }
        return { source, target, value };
      });

    const leedCategoryLinks = envfactors
      .filter((e) => e.gwp_total > 0)
      .map((e) => {
        const process_lca_stage = processData.find((p) => p.id === e.process_id)?.lca_stage;
        const source = getlcaStageCode(process_lca_stage)?.id;
        const target = leedCategoryBracket + e.type + leedCategoryBracket;
        const value = e.gwp_total;
        if (!source) {
          console.log("undefined source for leedCategoryLinks:", e, process_lca_stage);
        }
        return { source, target, value };
      });

    const materialCategoryLinks = envfactors
      .filter((e) => e.gwp_total > 0)
      .map((e) => {
        const target = materialCategoryBracket + e.type + materialCategoryBracket;
        const value = e.gwp_total;
        const process_lca_stage = processData.find((p) => p.id === e.process_id)?.lca_stage;
        const source = level2 === project_elments.value ? getlcaStageDescription(process_lca_stage)?.id : leedCategoryBracket + e.type + leedCategoryBracket;
        if (!source) {
          console.log("undefined source for materialCategoryLinks:", e, process_lca_stage);
        }
        return { source, target, value };
      });
    const validEnvfactors = envfactors.filter((e) => e.gwp_total > 0);
    const totalGwp = validEnvfactors.reduce((sum, e) => sum + e.gwp_total, 0);
    const sortedEnvfactors = [...validEnvfactors].sort((a, b) => b.gwp_total - a.gwp_total);

    let cumulativeGwp = 0;
    const topEnvfactors = [];
    const otherEnvfactors = [];

    for (const e of sortedEnvfactors) {
      if (cumulativeGwp < totalGwp * 0.9) {
        topEnvfactors.push(e);
        cumulativeGwp += e.gwp_total;
      } else {
        otherEnvfactors.push(e);
      }
    }

    setSuppressedMaterials(otherEnvfactors);

    const topMaterialLinks = topEnvfactors.map((e) => {
      const source = materialCategoryBracket + e.type + materialCategoryBracket;
      const target = materialBracket + e.description + materialBracket;
      const value = e.gwp_total;
      return { source, target, value };
    });

    const otherByType = {};
    otherEnvfactors.forEach((e) => {
      otherByType[e.type] = (otherByType[e.type] || 0) + e.gwp_total;
    });

    const otherMaterialLinks = Object.entries(otherByType).map(([type, value]) => {
      const source = materialCategoryBracket + type + materialCategoryBracket;
      const target = materialBracket + "Other Materials (Remaining <10%)" + materialBracket;
      return { source, target, value };
    });

    const materialLinks = [...topMaterialLinks, ...otherMaterialLinks];

    const topMaterialNodes = topEnvfactors.map((e) => ({
      id: materialBracket + e.description + materialBracket,
      label: e.description,
    }));

    const otherMaterialNode = otherEnvfactors.length > 0 ? [{
      id: materialBracket + "Other Materials (Remaining <10%)" + materialBracket,
      label: "Other Materials (Remaining <10%)"
    }] : [];

    const materialNodes = [];
    const seenNodes = new Set();
    [...topMaterialNodes, ...otherMaterialNode].forEach((n) => {
      if (!seenNodes.has(n.id)) {
        seenNodes.add(n.id);
        materialNodes.push(n);
      }
    });

    const materialCategoryNodes = [...new Set(envfactors.map((e) => e.type))].map((type) => ({ id: materialCategoryBracket + type + materialCategoryBracket, label: type }));

    const leedCategoryNodes = [...new Set(envfactors.map((e) => e.type))].map((type) => ({
      id: leedCategoryBracket + type + leedCategoryBracket,
      label: type,
    }));
    const projectElementNodes = [...new Set(processData.map((p) => p.lca_stage))].map((lca_stage) => {
      const ret = getlcaStageDescription(lca_stage);
      return ret;
    });
    const links = mergeLinks([...(level2 === project_elments.value ? projectElementLinks : leedCategoryLinks), ...materialCategoryLinks, ...materialLinks]);
    const filteredRootNodes = rootNodes.filter((n) => projectElementLinks.map((l) => l.source).includes(n.id));
    const nodes = [...filteredRootNodes, ...(level2 === project_elments.value ? projectElementNodes : leedCategoryNodes), ...materialCategoryNodes, ...materialNodes];
    const newData = { nodes, links };
    setSankeyData(newData);
    const totalValue = links.filter((l) => l.source && String(l.source).startsWith(rootNodeBracket)).reduce((sum, l) => sum + (l.value || 0), 0);
    setTotalRootValue(totalValue);
  }, [processData, level2, project_elments.value, getEnvFactors]);
  useEffect(() => {
    if (processData) {
      getSankeyData();
    }
  }, [processData, level2, getSankeyData]);
  return (
    <Paper variant="Table Container">
      <TableHeaderContainer>
        <TableTitleContainer>
          <Typography variant="Table Title" noWrap>
            {t("Sankey Breakdown")}
          </Typography>
        </TableTitleContainer>
      </TableHeaderContainer>
      <Box sx={{ p: 3, overflow: "auto", position: "relative" }}>
        {sankeyData.links.length > 0 && (
          <Box ref={ref}>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "space-between", paddingX: 4, alignItems: "center" }}>
              {levels.map((level, index) => (
                <Typography key={index} sx={{ fontWeight: 500, fontFamily: ORG_PALETTE.font.family, color: isDark ? "#4ade80" : tok.text.primary, fontSize: 18 }}>
                  {level}
                </Typography>
              ))}
            </Box>
            <Sankey data={sankeyData} />
          </Box>
        )}
        
        {suppressedMaterials.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              bottom: "24px",
              right: "24px",
              zIndex: 100,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                border: `1.2px solid ${isDark ? "#4ade80" : "#659d33"}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: isDark ? "#4ade80" : "#659d33",
                fontWeight: "600",
                fontSize: "11px",
                fontFamily: "Poppins, sans-serif",
                position: "relative",
                "&:hover .sankey-info-tooltip": {
                  display: "block !important",
                  opacity: "1 !important",
                  transform: "translateY(0) scale(1) !important",
                },
              }}
            >
              i
              <Box
                className="sankey-info-tooltip"
                sx={{
                  display: "none",
                  opacity: 0,
                  transform: "translateY(10px) scale(0.95)",
                  transition: "all 0.2s ease-in-out",
                  position: "absolute",
                  bottom: "26px",
                  right: "0",
                  width: "240px",
                  backgroundColor: isDark ? "#1e1e2e" : "#ffffff",
                  border: `1px solid ${isDark ? "#3f3f46" : "#cbd5e1"}`,
                  borderRadius: "8px",
                  boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.2)",
                  p: 1.5,
                  textAlign: "left",
                  zIndex: 9999,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontFamily: "Poppins, sans-serif",
                    color: isDark ? "#e4e4e7" : "#334155",
                    lineHeight: "1.5",
                  }}
                >
                  This diagram displays top materials contributing up to <strong>90% of total GWP</strong>. Remaining items are grouped under <strong>Other Materials</strong> to maintain a clean layout.
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
});

export default SankeyDiagram;
